import React, { PropsWithChildren, useEffect, useRef } from "react";
import {
    FireCMSPlugin,
    PluginFormActionProps,
    useSideDialogContext,
} from "@firecms/core";

const ENTITY_CACHE_PREFIX = "entity_cache::";

function eventElement(event: Event): HTMLElement | null {
    const target = event.target;
    if (target instanceof HTMLElement) {
        return target;
    }
    if (target instanceof Text) {
        return target.parentElement;
    }
    return null;
}

function cacheKeys(path: string, entityId: string | undefined): string[] {
    const keys = [`${path}#new`];
    if (entityId) {
        keys.push(`${path}/${entityId}`);
    }
    return keys;
}

/**
 * FireCMS "Discard Local Changes" ile aynı localStorage anahtarını siler.
 * Formex 300ms gecikmeyle yedeği tekrar yazabildiği için silmeyi tekrarlarız.
 */
function discardLocalChanges(path: string, entityId: string | undefined) {
    const remove = () => {
        try {
            for (const key of cacheKeys(path, entityId)) {
                window.localStorage.removeItem(`${ENTITY_CACHE_PREFIX}${key}`);
            }
        } catch {
            // private mode / erişim yok
        }
    };
    remove();
    window.setTimeout(remove, 0);
    window.setTimeout(remove, 350);
}

function isUnsavedLeaveDialog(dialog: HTMLElement): boolean {
    const text = dialog.textContent ?? "";
    return text.includes("Kaydetmeden kapatmak")
        || text.includes("Kaydedilmemiş değişiklikler")
        || text.includes("Unsaved changes")
        || text.includes("leave this page");
}

function confirmButtonOf(dialog: HTMLElement): HTMLElement | undefined {
    const buttons = Array.from(dialog.querySelectorAll("button"));
    return buttons.find((button) => {
        const label = button.textContent?.trim() ?? "";
        return label === "Kapat" || label === "Sil" || label === "OK" || label === "Ok";
    }) ?? buttons[buttons.length - 1];
}

/**
 * FireCMS X butonu dirty olsa bile force-close yapıyor.
 * Sadece değişiklik varsa X'i onay diyaloğuna yönlendirir;
 * değişiklik yoksa normal kapanışa izin verir (backdrop zaten böyle çalışır).
 */
function ConfirmCloseFormProvider({
    children,
    formContext,
    path,
    entityId,
}: PropsWithChildren<PluginFormActionProps>) {
    const { close } = useSideDialogContext();
    const dirtyRef = useRef(false);
    dirtyRef.current = Boolean(formContext?.formex?.dirty);
    const pathRef = useRef(path);
    const entityIdRef = useRef(entityId);
    const formContextRef = useRef(formContext);
    pathRef.current = path;
    entityIdRef.current = entityId;
    formContextRef.current = formContext;

    const discardOpenForm = () => {
        formContextRef.current?.formex?.resetForm();
        discardLocalChanges(pathRef.current, entityIdRef.current);
    };

    useEffect(() => {
        const onClickCapture = (event: MouseEvent) => {
            if (!dirtyRef.current) {
                return;
            }

            const target = eventElement(event);
            if (!target) {
                return;
            }

            const icon = target.closest(".material-icons");
            if (!icon || icon.textContent?.trim() !== "close") {
                return;
            }

            // Sadece üst bar'daki X; alan içi clear (close) ikonlarını atla
            const topBar = target.closest(".h-14");
            if (!topBar) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            close();
        };

        document.addEventListener("click", onClickCapture, true);
        return () => {
            document.removeEventListener("click", onClickCapture, true);
        };
    }, [close]);

    useEffect(() => {
        return () => {
            discardLocalChanges(pathRef.current, entityIdRef.current);
        };
    }, [path, entityId]);

    useEffect(() => {
        const onKapatCapture = (event: MouseEvent) => {
            const target = eventElement(event);
            if (!target) {
                return;
            }
            const dialog = target.closest<HTMLElement>("[role='dialog']");
            if (!dialog || !isUnsavedLeaveDialog(dialog)) {
                return;
            }
            const confirmButton = confirmButtonOf(dialog);
            if (!confirmButton || !confirmButton.contains(target)) {
                return;
            }
            if (confirmButton.textContent?.trim() === "Vazgeç") {
                return;
            }
            discardOpenForm();
        };

        document.addEventListener("click", onKapatCapture, true);
        return () => {
            document.removeEventListener("click", onKapatCapture, true);
        };
    }, []);

    useEffect(() => {
        const labelUnsavedConfirm = () => {
            document.querySelectorAll<HTMLElement>("[role='dialog']").forEach((dialog) => {
                if (!isUnsavedLeaveDialog(dialog)) {
                    return;
                }
                const heading = dialog.querySelector("h2, h6");
                if (heading && !heading.textContent?.trim()) {
                    heading.textContent = "Kaydedilmemiş değişiklikler";
                }
                const confirmButton = confirmButtonOf(dialog);
                if (
                    confirmButton &&
                    confirmButton.textContent?.trim() &&
                    confirmButton.textContent.trim() !== "Vazgeç" &&
                    confirmButton.textContent.trim() !== "Kapat"
                ) {
                    confirmButton.textContent = "Kapat";
                }
            });
        };

        const observer = new MutationObserver((mutations) => {
            const added = mutations.some((mutation) => mutation.addedNodes.length > 0);
            if (!added) {
                return;
            }
            observer.disconnect();
            labelUnsavedConfirm();
            observer.observe(document.body, { childList: true, subtree: true });
        });

        labelUnsavedConfirm();
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    return <>{children}</>;
}

/**
 * Değişiklik yapılmışsa ekleme/güncelleme panelini kapatmadan önce onay ister.
 */
export const confirmClosePlugin: FireCMSPlugin = {
    key: "confirm-close",
    i18n: {
        en: {
            unsaved_changes_title: "Kaydedilmemiş değişiklikler",
            are_you_sure_leave: "Kaydetmeden kapatmak istediğinize emin misiniz?",
            cancel: "Vazgeç",
        },
        tr: {
            unsaved_changes_title: "Kaydedilmemiş değişiklikler",
            are_you_sure_leave: "Kaydetmeden kapatmak istediğinize emin misiniz?",
            cancel: "Vazgeç",
        },
    },
    form: {
        provider: {
            Component: ConfirmCloseFormProvider,
        },
    },
};
